import { useRef, useState } from 'react'

import { API, constructApiUrl } from '../../../api/apis.config'
import FilterDrawer from '../../../components/elements/drawers/filter-drawer'
import DynamicDataTable, { DynamicTableRef } from '../../../components/elements/dynamic-table'
import { countFilters } from '../../../components/elements/dynamic-table/utils/countFilters'
import withPeriods, { WithPeriodsProps } from '../../../components/hoc/with-periods'
import { OlHostFamilyPricesFilter } from '../../../components/pages/ols/ol-host-family-prices/ol-host-family-prices-filter'
import useAuth from '../../../lib/hooks/use-auth'
import { useFilters } from '../../../lib/hooks/use-filters'
import { useModalManager } from '../../../lib/hooks/use-modal-manager'
import useTranslate from '../../../lib/hooks/use-translate'
import {
  OLHostFamilyPricePerNight,
  OLHostFamilyPricePerNightFilters
} from '../../../lib/types/models/ol/host-family-price-per-night/ol-host-family-price-per-night'
import { PeriodProperties } from '../../../lib/types/models/period/period.properties'
import {
  transformOlHostFamilyPriceFiltersToUrl,
  transformUrlToOlHostFamilyPriceFilters
} from '../../../lib/utils/filters-mapping/ol-host-family-price'
import { getHostFamilyPriceColumns, hasOtoPrices } from './utils'

const hostFamilyPricesInitialFilters: OLHostFamilyPricePerNightFilters = {}

const FeesHostFamilyPricesListPage = ({ periods }: WithPeriodsProps) => {
  const tableRef = useRef<DynamicTableRef<OLHostFamilyPricePerNight>>(null)
  const [isTableLoading, setIsTableLoading] = useState(false)
  // the OTO columns only make sense when LEC entered OTO prices on at least one listed price
  const [showOto, setShowOto] = useState(false)
  const { user } = useAuth()
  const { t } = useTranslate()
  const { modalState, toggleModal, closeModel } = useModalManager<'filters'>(['filters'])

  const { selectedFilter, filter, onFilterChange, resetFilters, onSelectedFilterChange } =
    useFilters<OLHostFamilyPricePerNightFilters>({
      initialFilters: {
        ...hostFamilyPricesInitialFilters,
        periods: PeriodProperties.getCurrentYearPeriods(periods ?? []).map((period) => period.name)
      },
      options: { allowSearchParam: true },
      transformFilterToUrl: transformOlHostFamilyPriceFiltersToUrl,
      transformUrlToFilter: transformUrlToOlHostFamilyPriceFilters
    })

  if (!user?.ol?.id) return null

  return (
    <div>
      <DynamicDataTable<OLHostFamilyPricePerNight, OLHostFamilyPricePerNightFilters>
        ref={tableRef}
        onLoadingChange={setIsTableLoading}
        onDataChange={(data) => setShowOto(data.some(hasOtoPrices))}
        title={'Host family fees'}
        isSearchable={false}
        permissions={{ delete: false }}
        columns={getHostFamilyPriceColumns('FEES', t, showOto)}
        allowSearchParam
        endpoint={constructApiUrl(API.ols.getOlHostFamilyPrices.url, { olId: user?.ol?.id })}
        filters={selectedFilter}
        filtersLength={countFilters(selectedFilter)}
        onFiltersClick={() => toggleModal('filters')}
      />

      {/* Filters Modal */}
      <FilterDrawer<OLHostFamilyPricePerNightFilters>
        isOpen={modalState.filters}
        selectedFilter={selectedFilter}
        isLoading={isTableLoading}
        onClose={() => closeModel('filters')}
        onReset={resetFilters}
        onApply={() => onSelectedFilterChange({ ...filter })}
        onFilterChange={onFilterChange}
      >
        <OlHostFamilyPricesFilter filter={filter} onFilterChange={onFilterChange} />
      </FilterDrawer>
    </div>
  )
}

export default withPeriods(FeesHostFamilyPricesListPage)
